import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';

// ── PDF Download Helper ───────────────────────────────────────────────────────
function downloadPDF(title, subtitle, contentFn) {
  const jsPDF  = require('jspdf').jsPDF;
  const doc    = new jsPDF();
  const pageWidth  = doc.internal.pageSize.getWidth();
  const margin     = 15;
  const maxWidth   = pageWidth - margin * 2;
  let y = 40;

  function cleanText(text) {
    if (!text) return '';
    return String(text)
      .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/^\*\s+/gm, '• ')
      .replace(/^-\s+/gm, '• ')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\*+/g, '')
      .replace(/#+/g, '')
      .trim();
  }

  // Header bar
  doc.setFillColor(10, 15, 30);
  doc.rect(0, 0, pageWidth, 28, 'F');

  // CrackPrep — left
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 212, 170);
  doc.text('CrackPrep', margin, 18);

  // Title — centre
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(title.replace(/_/g, ' '), pageWidth / 2, 18, { align: 'center' });

  // Current & Target jobs — top right
  if (subtitle) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(255, 255, 255);
    subtitle.split('\n').forEach((line, i) => {
      doc.text(line, pageWidth - margin, 13 + i * 8, { align: 'right' });
    });
  }

  const addHeading = (text) => {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 100, 80);
    doc.splitTextToSize(cleanText(text), maxWidth).forEach(line => {
      doc.text(line, margin, y); y += 7;
    });
    y += 2;
  };

  const addSubheading = (text) => {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 51, 51);
    doc.splitTextToSize(cleanText(text), maxWidth).forEach(line => {
      doc.text(line, margin, y); y += 6;
    });
    y += 1;
  };

  const addBody = (text) => {
    if (!text) return;
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 51, 51);
    doc.splitTextToSize(cleanText(text), maxWidth).forEach(line => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(line, margin, y); y += 5.5;
    });
    y += 3;
  };

  const addDivider = () => {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setDrawColor(0, 212, 170);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
  };

  contentFn(addHeading, addSubheading, addBody, addDivider);
  doc.save(title + '.pdf');
}

// ── Clean markdown for screen display ────────────────────────────────────────
function cleanMarkdown(text) {
  if (!text) return '';
  return String(text)
    .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\*\s+/gm, '• ')
    .replace(/^-\s+/gm, '• ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*+/g, '')
    .replace(/#+/g, '')
    .trim();
}

// ── Upgrade Prompt Component ──────────────────────────────────────────────────
function UpgradePrompt({ type, navigate }) {
  const plans = type === 'resume'
    ? [
        { name: 'ResumePlus', price: '₹79',  planType: 'resume_plus',  desc: '3 analyses' },
        { name: 'ResumePro',  price: '₹149', planType: 'resume_pro',   desc: '7 analyses' },
        { name: 'Elite',      price: '₹699', planType: 'elite',        desc: '7 analyses — everything' },
      ]
    : [
        { name: 'InterviewPlus', price: '₹199', planType: 'interview_plus', desc: '3 analyses' },
        { name: 'InterviewPro',  price: '₹399', planType: 'interview_pro',  desc: '7 analyses' },
        { name: 'Elite',         price: '₹699', planType: 'elite',          desc: '7 analyses — everything' },
      ];

  return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔒</div>
      <h3 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>
        {type === 'resume' ? 'Resume improvements not included' : 'Interview prep not included'}
      </h3>
      <p style={{ color: '#8899aa', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
        {type === 'resume'
          ? 'Purchase ResumePlus, ResumePro, or Elite to access detailed resume fixes.'
          : 'Purchase InterviewPlus, InterviewPro, or Elite to access full interview preparation.'}
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        {plans.map(plan => (
          <div key={plan.planType}
            style={{ background: '#141B2D', border: plan.name === 'Elite' ? '2px solid #00D4AA' : '1px solid #1a2640', borderRadius: '10px', padding: '1rem', minWidth: '120px', cursor: 'pointer' }}
            onClick={() => navigate('/pricing')}>
            <div style={{ color: '#00D4AA', fontWeight: 'bold', marginBottom: '0.2rem', fontSize: '0.85rem' }}>{plan.name}</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 'bold', marginBottom: '0.2rem' }}>{plan.price}</div>
            <div style={{ color: '#8899aa', fontSize: '0.75rem' }}>{plan.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Results() {
  const { state }     = useLocation();
  const navigate      = useNavigate();
  const isMobile      = useIsMobile();
  const [tab, setTab] = useState('ats');

  if (!state?.results) {
    return (
      <div style={{ background: '#0A0F1E', minHeight: '100vh', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial', padding: '1rem' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#8899aa', marginBottom: '1rem' }}>No results found.</p>
          <button onClick={() => navigate('/analyze')}
            style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
            Start New Analysis
          </button>
        </div>
      </div>
    );
  }

  const {
    atsScore, resumeImprovements, interviewPrep,
    currentTitle, currentCompany, targetTitle, targetCompany,
    planInfo
  } = state.results;

  const canResume    = planInfo?.canResume    ?? true;
  const canInterview = planInfo?.canInterview ?? true;

  const subtitle =
    'Current: ' + (currentTitle  || '') + ' @ ' + (currentCompany  || '') + '\n' +
    'Target:  ' + (targetTitle   || '') + ' @ ' + (targetCompany   || '');

  // ── Download handlers ──────────────────────────────────────────────────────
  const downloadResumePDF = () => {
    downloadPDF('Resume_Improvements', subtitle, (addHeading, addSubheading, addBody, addDivider) => {
      addHeading('Resume Improvement Report');
      addDivider();
      resumeImprovements?.sections?.forEach((s, i) => {
        addHeading('Section ' + (i + 1) + ': ' + s.title);
        addSubheading('Issue:');
        addBody(s.issue);
        addSubheading('Current Text:');
        addBody(s.currentText);
        addSubheading('Improved Version:');
        addBody(s.improvedText);
        addSubheading('Why this helps:');
        addBody(s.rationale);
        addDivider();
      });
      if (resumeImprovements?.missingElements?.length > 0) {
        addHeading('Missing Elements to Add');
        resumeImprovements.missingElements.forEach(el => addBody('• ' + el));
      }
    });
  };

  const downloadInterviewPDF = () => {
    downloadPDF('Interview_Prep', subtitle, (addHeading, addSubheading, addBody, addDivider) => {
      addHeading('Section 1: Competence Questions');
      addDivider();
      interviewPrep?.competence?.forEach((qa, i) => {
        addSubheading('Q' + (i + 1) + '. ' + qa.question);
        addBody(qa.answer);
        addDivider();
      });
      addHeading('Section 2: CV & Experience Questions');
      addDivider();
      interviewPrep?.cv?.forEach((qa, i) => {
        addSubheading('Q' + (i + 1) + '. ' + qa.question);
        addBody(qa.answer);
        addDivider();
      });
      addHeading('Section 3: HR & Culture Fit Questions');
      addDivider();
      interviewPrep?.hrFitment?.forEach((qa, i) => {
        addSubheading('Q' + (i + 1) + '. ' + qa.question);
        addBody(qa.answer);
        addDivider();
      });
    });
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const tabStyle = (t) => ({
    padding: isMobile ? '0.6rem 0.75rem' : '0.75rem 1.5rem',
    cursor: 'pointer',
    borderRadius: '8px',
    fontWeight: 'bold',
    border: 'none',
    fontSize: isMobile ? '0.8rem' : '0.95rem',
    background: tab === t ? '#00D4AA' : 'transparent',
    color:      tab === t ? '#0A0F1E' : '#8899aa',
    flex: isMobile ? 1 : 'none',
    textAlign: 'center',
  });

  const card = {
    background: '#141B2D',
    borderRadius: '12px',
    padding: isMobile ? '1rem' : '1.5rem',
    marginBottom: '1rem',
    border: '1px solid #1a2640',
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: '#0A0F1E', minHeight: '100vh', width: '100%', color: 'white', fontFamily: 'Arial, sans-serif', padding: isMobile ? '1rem' : '1.5rem', boxSizing: 'border-box' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <span onClick={() => navigate('/')}
          style={{ color: '#00D4AA', fontWeight: 'bold', fontSize: '1.3rem', cursor: 'pointer' }}>
          CrackPrep
        </span>
        {!isMobile && (
          <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#8899aa', lineHeight: 1.6 }}>
            <div>Current: <span style={{ color: 'white' }}>{currentTitle} @ {currentCompany}</span></div>
            <div>Target: <span style={{ color: '#00D4AA' }}>{targetTitle} @ {targetCompany}</span></div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/history')}
            style={{ background: 'transparent', color: '#8899aa', border: '1px solid #1a2640', padding: '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
            History
          </button>
          <button onClick={() => navigate('/analyze')}
            style={{ background: 'transparent', color: '#00D4AA', border: '1px solid #00D4AA', padding: '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
            New
          </button>
        </div>
      </div>

      {/* Target info on mobile */}
      {isMobile && (
        <div style={{ background: '#141B2D', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', border: '1px solid #1a2640', fontSize: '0.8rem' }}>
          <div style={{ color: '#8899aa' }}>Current: <span style={{ color: 'white' }}>{currentTitle} @ {currentCompany}</span></div>
          <div style={{ color: '#8899aa', marginTop: '0.25rem' }}>Target: <span style={{ color: '#00D4AA' }}>{targetTitle} @ {targetCompany}</span></div>
        </div>
      )}

      {/* Plan info bar */}
      {planInfo && (
        <div style={{ background: '#141B2D', borderRadius: '8px', padding: '0.6rem 1rem', marginBottom: '1rem', border: '1px solid #1a2640', display: 'flex', gap: '1rem', fontSize: '0.8rem', flexWrap: 'wrap' }}>
          {canResume && (
            <span>📝 Resume left: <strong style={{ color: '#00D4AA' }}>{planInfo.resumeRemaining}</strong></span>
          )}
          {canInterview && (
            <span>🎯 Interview left: <strong style={{ color: '#00D4AA' }}>{planInfo.interviewRemaining}</strong></span>
          )}
          {!canResume && !canInterview && (
            <span style={{ color: '#f5a623' }}>⚠ No analyses left — <span style={{ color: '#00D4AA', cursor: 'pointer' }} onClick={() => navigate('/pricing')}>upgrade</span></span>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', background: '#141B2D', padding: '0.4rem', borderRadius: '10px', border: '1px solid #1a2640', width: '100%', boxSizing: 'border-box' }}>
        <button style={tabStyle('ats')}       onClick={() => setTab('ats')}>📊 {isMobile ? 'ATS' : 'ATS Score'}</button>
        <button style={tabStyle('resume')}    onClick={() => setTab('resume')}>📝 {isMobile ? 'Resume' : 'Resume Fixes'}</button>
        <button style={tabStyle('interview')} onClick={() => setTab('interview')}>🎯 {isMobile ? 'Interview' : 'Interview Prep'}</button>
      </div>

      {/* Download buttons */}
      <div style={{ marginBottom: '1rem' }}>
        {tab === 'resume' && canResume && resumeImprovements && (
          <div>
            <button onClick={downloadResumePDF}
              style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: '0.65rem 1.25rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem', width: isMobile ? '100%' : 'auto' }}>
              ⬇ Download Resume Report PDF
            </button>
            <p style={{ color: '#8899aa', fontSize: '0.75rem', marginTop: '0.4rem' }}>
              💡 Best viewed in <a href="https://get.adobe.com/reader/" target="_blank" rel="noreferrer" style={{ color: '#00D4AA' }}>Adobe Acrobat Reader</a>
            </p>
          </div>
        )}
        {tab === 'interview' && canInterview && interviewPrep && (
          <div>
            <button onClick={downloadInterviewPDF}
              style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: '0.65rem 1.25rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem', width: isMobile ? '100%' : 'auto' }}>
              ⬇ Download Interview Prep PDF
            </button>
            <p style={{ color: '#8899aa', fontSize: '0.75rem', marginTop: '0.4rem' }}>
              💡 Best viewed in <a href="https://get.adobe.com/reader/" target="_blank" rel="noreferrer" style={{ color: '#00D4AA' }}>Adobe Acrobat Reader</a>
            </p>
          </div>
        )}
      </div>

      {/* ── ATS Tab ── */}
      {tab === 'ats' && atsScore && (
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', ...card }}>
            <div style={{ textAlign: 'center', minWidth: isMobile ? '80px' : '120px' }}>
              <div style={{ fontSize: isMobile ? '3rem' : '4.5rem', fontWeight: 'bold', color: atsScore.score >= 70 ? '#00D4AA' : atsScore.score >= 50 ? '#f5a623' : '#ff6b6b', lineHeight: 1 }}>
                {atsScore.score}
              </div>
              <div style={{ color: '#8899aa', fontSize: '0.8rem' }}>out of 100</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.4rem', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                {atsScore.score >= 70 ? '✅ Good ATS match' : atsScore.score >= 50 ? '⚠️ Needs improvement' : '❌ Low ATS match'}
              </div>
              <p style={{ color: '#8899aa', fontSize: isMobile ? '0.8rem' : '0.9rem', lineHeight: 1.5, margin: 0 }}>{cleanMarkdown(atsScore.summary)}</p>
            </div>
          </div>

          <div style={{ ...card }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>Score Breakdown</h3>
            {Object.entries(atsScore.breakdown || {}).map(([key, val]) => (
              <div key={key} style={{ marginBottom: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ color: '#8899aa', fontSize: '0.85rem', textTransform: 'capitalize' }}>
                    {key.replace(/([A-Z])/g, ' $1')}
                  </span>
                  <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: val >= 70 ? '#00D4AA' : val >= 50 ? '#f5a623' : '#ff6b6b' }}>
                    {val}%
                  </span>
                </div>
                <div style={{ background: '#0A0F1E', borderRadius: '4px', height: '7px' }}>
                  <div style={{ background: val >= 70 ? '#00D4AA' : val >= 50 ? '#f5a623' : '#ff6b6b', width: val + '%', height: '7px', borderRadius: '4px' }} />
                </div>
              </div>
            ))}
          </div>

          {atsScore.missingKeywords?.length > 0 && (
            <div style={card}>
              <h3 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Missing Keywords</h3>
              <p style={{ color: '#8899aa', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Add these to your resume:</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {atsScore.missingKeywords.map((k, i) => (
                  <span key={i} style={{ background: '#0A0F1E', border: '1px solid #00D4AA', color: '#00D4AA', padding: '0.25rem 0.6rem', borderRadius: '20px', fontSize: '0.8rem' }}>
                    {k}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Resume Tab ── */}
      {tab === 'resume' && (
        canResume && resumeImprovements ? (
          <div style={{ width: '100%' }}>
            {resumeImprovements.sections?.map((s, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#00D4AA', marginBottom: '0.75rem', fontSize: '1rem' }}>{s.title}</h3>
                <div style={{ background: '#1a0a0a', border: '1px solid #3a1010', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                  <p style={{ color: '#ff6b6b', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>⚠ ISSUE</p>
                  <p style={{ color: '#ffaaaa', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>{cleanMarkdown(s.issue)}</p>
                </div>
                <div style={{ background: '#0A0F1E', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                  <p style={{ color: '#8899aa', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '0.4rem' }}>CURRENT</p>
                  <p style={{ color: '#ccc', fontSize: '0.85rem', lineHeight: 1.6, margin: 0 }}>{cleanMarkdown(s.currentText)}</p>
                </div>
                <div style={{ background: '#0d2018', border: '1px solid #1a4030', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                  <p style={{ color: '#00D4AA', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '0.4rem' }}>✓ IMPROVED</p>
                  <p style={{ color: '#ccc', fontSize: '0.85rem', lineHeight: 1.6, margin: 0 }}>{cleanMarkdown(s.improvedText)}</p>
                </div>
                <div style={{ borderTop: '1px solid #1a2640', paddingTop: '0.6rem' }}>
                  <p style={{ color: '#8899aa', fontSize: '0.8rem', fontStyle: 'italic', margin: 0, lineHeight: 1.5 }}>
                    💡 {cleanMarkdown(s.rationale)}
                  </p>
                </div>
              </div>
            ))}
            {resumeImprovements.missingElements?.length > 0 && (
              <div style={card}>
                <h3 style={{ color: '#00D4AA', marginBottom: '0.75rem', fontSize: '1rem' }}>Missing Elements to Add</h3>
                {resumeImprovements.missingElements.map((el, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#00D4AA', marginTop: '2px' }}>•</span>
                    <p style={{ color: '#8899aa', margin: 0, fontSize: '0.85rem', lineHeight: 1.5 }}>{cleanMarkdown(el)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <UpgradePrompt type="resume" navigate={navigate} />
        )
      )}

      {/* ── Interview Tab ── */}
      {tab === 'interview' && (
        canInterview && interviewPrep ? (
          <div style={{ width: '100%' }}>
            {[
              { key: 'competence', label: '🧠 Competence',       color: '#00D4AA' },
              { key: 'cv',         label: '📄 CV Questions',     color: '#5b8cff' },
              { key: 'hrFitment',  label: '🤝 HR & Culture Fit', color: '#f5a623' },
            ].map(section => (
              <div key={section.key} style={{ marginBottom: '2rem' }}>
                <h2 style={{ color: section.color, marginBottom: '0.75rem', fontSize: isMobile ? '1rem' : '1.2rem', borderBottom: '2px solid ' + section.color, paddingBottom: '0.4rem', display: 'inline-block' }}>
                  {section.label}
                </h2>
                {interviewPrep[section.key]?.map((qa, i) => (
                  <div key={i} style={{ ...card, borderLeft: '3px solid ' + section.color }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '0.6rem', lineHeight: 1.5, fontSize: isMobile ? '0.85rem' : '0.95rem', color: 'white' }}>
                      Q{i + 1}. {cleanMarkdown(qa.question)}
                    </p>
                    <p style={{ color: '#aabbcc', lineHeight: 1.7, fontSize: isMobile ? '0.82rem' : '0.9rem', margin: 0, whiteSpace: 'pre-line' }}>
                      {cleanMarkdown(qa.answer)}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <UpgradePrompt type="interview" navigate={navigate} />
        )
      )}
    </div>
  );
}